import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  Check,
  Lock,
  Activity,
  Clock,
  FileText,
  Users,
  Brain,
  BarChart3,
  ChevronDown,
} from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Footer from "../../components/Footer/Footer";
import Testimonials from "../../components/Testimonials/Testimonials";
import { useCurrency } from "../../hooks/useCurrency";

const TITLE = "AI Powered Employee Monitoring & Workforce Analytics | TeamGrid";
const DESCRIPTION =
  "Get real time dashboards, automatic time tracking, AI work summaries, and deep team insights. Make smarter workforce decisions with TeamGrid. Book a demo today.";
const CANONICAL = "https://teamgrid.ai/ai-workforce-intelligence";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid is an AI powered team intelligence platform that analyzes real work patterns and productivity signals to generate summaries, insights, and automation recommendations for teams.",
      },
    },
    {
      "@type": "Question",
      name: "Does TeamGrid invade privacy?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid is built with a privacy first approach. It focuses on productivity insights and work patterns rather than intrusive surveillance.",
      },
    },
    {
      "@type": "Question",
      name: "How does the tracking work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid automatically tracks application usage, time patterns, and productivity signals to provide real time workforce intelligence without manual reporting.",
      },
    },
  ],
};

const FEATURES = [
  {
    title: "Activity Monitoring",
    description:
      "Understand how work happens through application usage, focus time, and work pattern analysis.",
    icon: Activity,
  },
  {
    title: "Automatic Time Tracking",
    description:
      "Runs continuously with zero manual input, ensuring every billable minute is captured accurately.",
    icon: Clock,
  },
  {
    title: "AI Work Summaries",
    description:
      "Daily and weekly summaries showing what was worked on and where attention shifted.",
    icon: FileText,
  },
  {
    title: "Team & Project Visibility",
    description:
      "Track productivity across teams and projects to understand progress, capacity, and delivery trends.",
    icon: Users,
  },
  {
    title: "Ask TeamGrid (AI)",
    description:
      "Get instant answers about overall team activity or individual performance using natural-language questions.",
    icon: Brain,
  },
  {
    title: "Organizational Intelligence",
    description:
      "Detect risks and improvement areas by analyzing daily work patterns across the organization.",
    icon: BarChart3,
  },
];

const TRADITIONAL_ITEMS = [
  "Manual reporting",
  "No real-time visibility",
  "Limited performance insights",
  "Basic attendance tracking",
];

const TEAMGRID_ITEMS = [
  "AI-powered analytics",
  "Live dashboards",
  "Deep workforce intelligence",
  "Smart activity tracking",
];

const getPricingPlans = (formatPrice) => [
  // {
  //   name: "Starter",
  //   tagline: "For small teams getting started",
  //   price: formatPrice('free'),
  //   isFree: true,
  //   period: "/ 5 days",
  //   features: [
  //     "Up to 5 team members",
  //     "Monthly and Daily Reports",
  //     "Team Management and Project Tracking",
  //   ],
  //   cta: "Start Trial",
  //   href: "/register",
  //   primary: false,
  //   popular: false,
  // },
  {
    name: "Team",
    tagline: "For growing teams",
    price: formatPrice('standard'),
    period: "/ user / month",
    features: [
      "Unlimited team members",
      "Monthly and Daily Reports",
      "Team Management and Project Tracking",
    ],
    cta: "Get Started",
    href: "/register",
    primary: false,
    popular: false,
  },
  {
    name: "Advanced",
    tagline: "For teams that want AI power",
    price: formatPrice('premium'),
    period: "/ user / month",
    features: [
      "Everything in Basic",
      "Ask TeamGrid",
      "Organization Intelligence",
    ],
    cta: "Get Started",
    href: "/register",
    primary: true,
    popular: true,
  },
  {
    name: "Enterprise",
    tagline: "For large organizations",
    price: "Custom",
    period: "",
    features: ["All Features", "Custom integrations", "Custom Reports"],
    cta: "Contact Sales",
    href: "/contact-us",
    primary: false,
    popular: false,
  },
];

const FAQ_ITEMS = [
  {
    q: "What is TeamGrid?",
    a: "TeamGrid is an AI-powered team intelligence platform that analyses real work patterns and productivity signals to generate summaries, insights, and automation recommendations for teams.",
  },
  {
    q: "Does TeamGrid invade privacy?",
    a: "TeamGrid is designed with privacy-first principles. We focus on work patterns and productivity signals, not surveillance.",
  },
  {
    q: "How does the tracking work?",
    a: "Tracking runs in the background with minimal manual input, capturing application usage and focus time to power insights and reports.",
  },
];

const AlternateLanding = ({ onSwitchBack }) => {
  const { formatPrice } = useCurrency();
  const PRICING_PLANS = getPricingPlans(formatPrice);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [demoForm, setDemoForm] = useState({
    fullName: "",
    email: "",
    company: "",
    phone: "",
  });
  const [openFaq, setOpenFaq] = useState(0);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [activeErrorFormId, setActiveErrorFormId] = useState(null);
  const [currentPricingSlide, setCurrentPricingSlide] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isAiWorkforce =
    location.pathname.replace(/\/+$/, "") === "/ai-workforce-intelligence";
  const recaptchaRef = React.useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    e.nativeEvent.stopPropagation(); // Block GTM's native form listener — manual dataLayer.push handles tracking

    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA verification.");
      return;
    }

    const submittedFormId = e.target?.id || "demo-request-ai-workforce-intelligence";

    // Honeypot check — bots fill hidden fields, real users never do
    const honeypot = e.target?.elements?.website?.value || "";
    if (honeypot.trim() !== "") return;

    const fullName = (demoForm.fullName || "").trim();
    const email = (demoForm.email || "").trim();
    const company = (demoForm.company || "").trim();

    const phone = (demoForm.phone || "").trim();
    const errors = {};
    if (!fullName) errors.fullName = "Full Name is required.";
    if (!email) {
      errors.email = "Work Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!company || company.length < 2) errors.company = "Company name is required (min 2 characters).";
    if (!phone) {
      errors.phone = "Phone Number is required.";
    } else if (phone.length !== 10) {
      errors.phone = "Please enter a valid 10-digit phone number.";
    }
    setFormErrors(errors);
    setActiveErrorFormId(submittedFormId);
    if (Object.keys(errors).length > 0) return;

    setDemoSubmitting(true);

    try {
      const payload = {
        type: "contact_us",
        inquiryType: "Request A Demo",
        fullName,
        email,
        companyName: (demoForm.company || "").trim(),
        subject: "Request a Demo",
        message: `Demo request from landing page. Phone: ${demoForm.phone || "—"}`,
        recaptchaToken,
      };
      const response = await fetch("/api/v1/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message || `Request failed (${response.status})`;
        throw new Error(errorMessage);
      }
      // Push to GTM only on successful submission
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "form_submit",
        form_id: submittedFormId,
        form_text: "Request a Demo",
        form_url: window.location.href,
      });
      setDemoSubmitted(true);
      toast.success("Request received. We'll be in touch shortly.");
      setTimeout(() => {
        navigate("/thanks-demo-request", { state: { email } });
      }, 1000);
    } catch (err) {
      console.error("Demo request error:", err);
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setDemoSubmitting(false);
      // Reset reCAPTCHA so user can retry
      setRecaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  return (
    <div
      className="min-h-screen bg-white text-[#232323] overflow-x-hidden overflow-y-visible"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <PublicHeader minimal={isAiWorkforce} />

      {/* Hero Section - matches design spec */}
      <section
        className="relative flex flex-col justify-center items-center w-full overflow-hidden pt-24 pb-12 px-4 md:pt-[120px] md:pb-[80px] md:px-[80px]"
        style={{
          isolation: "isolate",
        }}
      >
        <div
          className="flex flex-col lg:flex-row items-start w-full max-w-[1232px] mx-auto px-0 gap-12 lg:gap-[60px] relative z-10"
          style={{ gap: 60 }}
        >
          {/* Left column - headline + copy */}
          <div
            className="flex flex-col items-start flex-1 max-w-[546px]"
            style={{ paddingBottom: 32, gap: "22.8px" }}
          >
            <h1
              className="font-extrabold text-black max-w-[546px]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: "clamp(24px, 5vw, 55px)",
                lineHeight: "110%",
                letterSpacing: "-0.01em",
                color: "#000000",
              }}
            >
              {/* Unlock Smarter Workforce Decisions */}
              AI-Powered Employee Monitoring Software Built for High-Performing Teams{" "}
              <span className="whitespace-nowrap">
                with <span style={{ color: "#00D4BD" }}>TeamGrid</span>
              </span>
            </h1>
            <p
              className="text-black max-w-[480px]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: 18,
                lineHeight: "29px",
                color: "#000000",
              }}
            >
              TeamGrid helps organizations track performance, improve
              productivity, and make data-driven workforce decisions.
            </p>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: 14,
                lineHeight: "22px",
                color: "#A0A0A0",
              }}
            >
              No credit card required • Full feature access • 5-minute setup
            </p>
          </div>
          {/* Request a Demo card */}
          <div
            className="w-full flex-shrink-0 bg-white overflow-hidden p-6 sm:p-10"
            style={{
              maxWidth: 546,
              boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.2)",
              borderRadius: 16,
            }}
          >
            {demoSubmitted ? (
              <div className="flex flex-col items-center justify-center text-center py-8" style={{ minHeight: 280 }}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(0, 212, 189, 0.15)" }}>
                  <Check className="w-7 h-7" style={{ color: "#00D4BD" }} />
                </div>
                <h3
                  className="font-bold text-[#1F2937] mb-2"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: 24,
                    lineHeight: "38px",
                  }}
                >
                  Thank you!
                </h3>
                <p
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#6B7280",
                  }}
                >
                  We&apos;ve received your request and will be in touch shortly.
                </p>
              </div>
            ) : (
              <>
                <h3
                  className="font-bold text-[#1F2937] mb-6"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: 24,
                    lineHeight: "38px",
                  }}
                >
                  Request a Demo
                </h3>
                <form
                  id="demo-request-ai-workforce-intelligence"
                  onSubmit={handleDemoSubmit}
                  className="flex flex-col w-full"
                  style={{ gap: 15 }}
                >
                  {/* Honeypot field — hidden from real users, bots will fill it */}
                  <input
                    type="text"
                    name="website"
                    style={{ display: "none", visibility: "hidden", position: "absolute", left: "-9999px" }}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                  {[
                    {
                      key: "fullName",
                      label: "Full Name",
                      placeholder: "John Doe",
                      required: true,
                    },
                    {
                      key: "email",
                      label: "Work Email",
                      placeholder: "john@company.com",
                      required: true,
                    },
                    {
                      key: "company",
                      label: "Company Name",
                      placeholder: "Acme Inc.",
                      required: true,
                    },
                    {
                      key: "phone",
                      label: "Phone Number",
                      placeholder: "9876543210",
                      required: true,
                    },
                  ].map(({ key, label, placeholder, required }) => (
                    <div
                      key={key}
                      className="flex flex-col w-full"
                      style={{ gap: 8 }}
                    >
                      <label
                        className="block w-full"
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 500,
                          fontSize: 14,
                          lineHeight: "22px",
                          color: "#374151",
                        }}
                      >
                        {label}{required && <span style={{ color: "#EF4444", marginLeft: 2 }}>*</span>}
                      </label>
                      <input
                        type={key === "email" ? "email" : "text"}
                        inputMode={key === "phone" ? "numeric" : undefined}
                        maxLength={key === "phone" ? 10 : undefined}
                        value={demoForm[key]}
                        onChange={(e) => {
                          const val = key === "phone"
                            ? e.target.value.replace(/[^0-9]/g, "").slice(0, 10)
                            : e.target.value;
                          setDemoForm((s) => ({ ...s, [key]: val }));
                          if (formErrors[key]) setFormErrors((prev) => ({ ...prev, [key]: undefined }));
                        }}
                        placeholder={placeholder}
                        className="w-full rounded-lg border bg-white box-border placeholder:text-[#6B7280]"
                        style={{
                          padding: 12,
                          fontFamily: "Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: 16,
                          lineHeight: "18px",
                          color: "#1F2937",
                          borderColor: (formErrors[key] && activeErrorFormId === "demo-request-ai-workforce-intelligence") ? "#EF4444" : "#E5E7EB",
                        }}
                      />
                      {formErrors[key] && activeErrorFormId === "demo-request-ai-workforce-intelligence" && (
                        <span style={{ color: "#EF4444", fontSize: 12, marginTop: 2 }}>
                          {formErrors[key]}
                        </span>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onChange={(token) => setRecaptchaToken(token)}
                      onExpired={() => setRecaptchaToken(null)}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={demoSubmitting || !recaptchaToken}
                    className="w-full rounded-lg text-center font-bold text-black disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{
                      background: "#00D4BD",
                      padding: "13px 24px 12px",
                      height: 40,
                      fontFamily: "Arial, sans-serif",
                      fontWeight: 700,
                      fontSize: "13.3px",
                      lineHeight: "15px",
                    }}
                  >
                    {demoSubmitting ? "Submitting..." : "Request a Demo"}
                  </button>
                </form>
                <p
                  className="flex items-center justify-center gap-1.5 mt-6 text-center"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: "19px",
                    color: "#6B7280",
                  }}
                >
                  <Lock
                    className="flex-shrink-0"
                    style={{ width: 12, height: 12, color: "#6B7280" }}
                  />
                  We respect your privacy. Your info is safe.
                </p>
              </>
            )}
          </div>
        </div>
        {/* Gradient blob - behind card, upper right */}
        <div
          className="absolute right-0 top-0 pointer-events-none w-[500px] h-[500px] -translate-y-1/4 translate-x-1/4"
          style={{
            background:
              "radial-gradient(70.71% 70.71% at 50% 50%, rgba(0, 212, 189, 0.15) 0%, rgba(0, 212, 189, 0) 70%)",
            borderRadius: 300,
            zIndex: 0,
          }}
          aria-hidden
        />
      </section>

      {/* Social Proof - same structure as Landing logo row */}
      <section
        className="relative z-10 flex flex-col items-center w-full py-8 md:py-10 px-4 md:px-6"
        style={{ background: "#F9FAFB" }}
      >
        <p
          className="text-center font-semibold text-[14.4px] leading-[23px] tracking-widest uppercase mb-8"
          style={{ color: "#6B7280" }}
        >
          Trusted by 500+ founders, managers, and growing teams
        </p>
        <div className="max-w-[1320px] mx-auto w-full">
          <div className="relative max-w-[1100px] mx-auto">
            <style>{`
              @keyframes scroll-left {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              .logo-scroll {
                animation: scroll-left 30s linear infinite;
              }
              .logo-scroll:hover {
                animation-play-state: paused;
              }
            `}</style>
            <div className="flex overflow-hidden">
              <div className="flex logo-scroll gap-8 sm:gap-12 lg:gap-16 items-center whitespace-nowrap">
                {(() => {
                  const logos = [
                    {
                      src: "/Brand_gridd.webp",
                      alt: "BrandGrid",
                      label: "BrandGrid",
                    },
                    {
                      src: "/saeller_777.webp",
                      alt: "Seller 7",
                      label: "Seller 7",
                    },
                    {
                      src: "/ecomsellertoollogo_.webp",
                      alt: "Ecomsellertool",
                      label: "Ecomsellertool",
                    },
                    { src: "/Cord4_11111.webp", alt: "CORD4", label: "CORD4" },
                    { src: "/grow8_1.webp", alt: "grow 8", label: "" },
                  ];
                  return [...logos, ...logos].map((logo, index) => (
                    <div
                      key={`logo-${index}`}
                      className="flex-shrink-0 flex items-center gap-2"
                    >
                      <div className="flex-shrink-0 flex items-center justify-center">
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          className="h-6 sm:h-7 md:h-8 object-contain opacity-100 transition-opacity duration-300"
                        />
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About - Privacy-First + See the Difference */}
      <section
        className="flex flex-col justify-center items-center w-full py-12 sm:py-16 md:py-20 px-4 sm:px-12 md:px-[80px]"
        style={{ gap: "var(--gap, 48px)" }}
      >
        <div className="max-w-[1760px] mx-auto text-center w-full">
          <h2 className="font-bold text-[32px] sm:text-[36px] leading-[40px] sm:leading-[58px] tracking-[-0.72px] text-[#1F2937] mb-5">
            Privacy-First Workforce Intelligence
          </h2>
          <p className="text-[16px] sm:text-[18px] leading-[26px] sm:leading-[32px] text-[#6B7280] max-w-[754px] mx-auto mb-10 sm:mb-12">
            TeamGrid is a platform that helps leaders track employee
            performance, uncover productivity trends, and make better decisions{" "}
            <span style={{ color: "#00D4BD" }}>without micromanagement.</span>
          </p>
          <div
            className="rounded-[20px] p-12 sm:p-16 flex flex-col items-center gap-8 max-w-[988px] mx-auto"
            style={{ background: "#000000" }}
          >
            <h3
              className="font-bold text-[36px] leading-[58px] tracking-[-0.72px]"
              style={{ color: "rgba(255, 255, 255, 0.9)" }}
            >
              See the Difference
            </h3>
            <p
              className="text-[16px] leading-[26px]"
              style={{ color: "rgba(255, 255, 255, 0.7)" }}
            >
              Why smart teams switch to TeamGrid
            </p>
            <div className="flex flex-row flex-wrap justify-center gap-10 sm:gap-12">
              <div className="flex flex-col items-center min-w-[200px]">
                <h4
                  className="font-bold text-[18.7px] leading-[30px] mb-4"
                  style={{ color: "rgba(255, 255, 255, 0.9)" }}
                >
                  Traditional Tools
                </h4>
                <ul
                  className="space-y-2 text-[16px] leading-[26px]"
                  style={{ color: "rgba(255, 255, 255, 0.7)" }}
                >
                  {TRADITIONAL_ITEMS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col items-center min-w-[200px]">
                <h4
                  className="font-bold text-[18.7px] leading-[30px] mb-4"
                  style={{ color: "#00D4BD" }}
                >
                  TeamGrid
                </h4>
                <ul
                  className="space-y-2 text-[16px] font-semibold leading-[26px]"
                  style={{ color: "rgba(255, 255, 255, 0.9)" }}
                >
                  {TEAMGRID_ITEMS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features — spec: 80px padding, 48px gap, cards 12px radius, 33px padding, icon 48x48 radius 8px */}
      <section
        className="flex flex-col justify-center items-center w-full py-12 md:py-[80px] px-4 md:px-[80px]"
        style={{
          background: "#F9FAFB",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          className="max-w-[1200px] w-full mx-auto flex flex-col items-center"
          style={{ gap: 48 }}
        >
          <div
            className="flex flex-col items-center text-center w-full"
            style={{ gap: 14.8, maxWidth: 600 }}
          >
            <h2
              className="font-bold text-[#1F2937] text-center"
              style={{
                fontSize: 36,
                lineHeight: "58px",
                letterSpacing: "-0.72px",
              }}
            >
              Built for Growing Businesses
            </h2>
            <p
              className="text-center"
              style={{
                fontSize: 16,
                lineHeight: "26px",
                color: "#6B7280",
                fontWeight: 400,
              }}
            >
              Everything you need from employee monitoring and time tracking
              software designed for adoption and clarity.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
            {FEATURES.map((f, index) => {
              const iconImages = [
                "/3_icon_alp.webp",
                "/4_icon_alp.webp",
                "/5_icon_alp.webp",
              ];
              const useImage = index >= 3 ? iconImages[index - 3] : null;
              return (
                <div
                  key={f.title}
                  className="border border-[#E5E7EB] bg-white flex flex-col box-border"
                  style={{ padding: 33, borderRadius: 12 }}
                >
                  {useImage ? (
                    <div
                      className="w-12 h-12 flex items-center justify-center flex-shrink-0 overflow-hidden"
                      style={{
                        background: "rgba(0, 212, 189, 0.1)",
                        borderRadius: 8,
                      }}
                    >
                      <img
                        src={useImage}
                        alt={f.title}
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  ) : (
                    <div
                      className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                      style={{
                        background: "rgba(0, 212, 189, 0.1)",
                        borderRadius: 8,
                      }}
                    >
                      <f.icon
                        className="w-6 h-6"
                        style={{ color: "#00D4BD" }}
                        strokeWidth={2}
                      />
                    </div>
                  )}
                  <h4
                    className="font-bold text-[#1F2937]"
                    style={{ fontSize: 18, lineHeight: "29px", marginTop: 23 }}
                  >
                    {f.title}
                  </h4>
                  <p
                    className="text-[#6B7280] line-clamp-3"
                    style={{
                      fontSize: 15.2,
                      lineHeight: "23px",
                      fontWeight: 400,
                      marginTop: 11,
                    }}
                  >
                    {f.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials — same full-bleed section as main Landing */}
      <Testimonials
        title="Trusted by Leading Teams"
        subtitle="See what our customers say about TeamGrid"
      />

      {/* Pricing — Frame 15 spec: extra top padding so "MOST POPULAR" badge is not clipped */}
      {/* Hidden on /ai-workforce-intelligence */}
      {!isAiWorkforce && (
      <section
        className="flex flex-col items-center w-full overflow-visible pt-16 pb-12 px-4 md:pt-[100px] md:pb-[80px] md:px-[80px] gap-10 md:gap-16"
        style={{
          background: "rgba(0, 0, 0, 0.02)",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          className="flex flex-col items-center text-center"
          style={{ gap: 20, maxWidth: 680 }}
        >
          <h2
            className="font-extrabold text-black text-center"
            style={{
              fontSize: 48,
              lineHeight: "57px",
              letterSpacing: "-1.56px",
            }}
          >
            Simple, Honest Pricing
          </h2>
          <p
            className="text-center"
            style={{
              fontSize: 20,
              lineHeight: "31px",
              color: "rgba(0, 0, 0, 0.6)",
              fontWeight: 400,
            }}
          >
            Start free. Upgrade when you're ready. No hidden fees, no surprises.
          </p>
        </div>
        <div className="relative w-full max-w-[1200px] mx-auto z-10 flex flex-col items-center">
          {/* Mobile Arrows */}
          <div className="flex md:hidden w-full items-center justify-between absolute top-[55%] -translate-y-1/2 px-2 sm:px-4 z-20 pointer-events-none">
            <button 
              onClick={() => setCurrentPricingSlide(s => Math.max(0, s - 1))}
              disabled={currentPricingSlide === 0}
              className="pointer-events-auto w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-200 disabled:opacity-0 transition-opacity"
              aria-label="Previous pricing"
            >
              <ChevronDown className="w-6 h-6 rotate-90 text-black" />
            </button>
            <button 
              onClick={() => setCurrentPricingSlide(s => Math.min(3, s + 1))}
              disabled={currentPricingSlide === 3}
              className="pointer-events-auto w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-200 disabled:opacity-0 transition-opacity"
              aria-label="Next pricing"
            >
              <ChevronDown className="w-6 h-6 -rotate-90 text-black" />
            </button>
          </div>

          <div
            className="w-[282px] md:w-full overflow-hidden md:overflow-visible"
            style={{ paddingTop: 48 }}
          >
            <div 
              className="flex flex-nowrap md:justify-center items-stretch gap-0 md:gap-6 transition-transform duration-300 ease-in-out md:!transform-none"
              style={{
                transform: `translateX(-${currentPricingSlide * 282}px)`
              }}
            >
          {/* FREE_PLAN_HIDDEN: .filter() hides the Free/Starter card. Remove the filter to re-enable. See FREE_PLAN_TOGGLE.md */}
          {PRICING_PLANS.filter((plan) => !plan.isFree).map((plan) => (
            <div
              key={plan.name}
              className="relative flex flex-col flex-shrink-0 bg-white box-border"
              style={{
                width: 282,
                minHeight: 546.75,
                padding: "36px 37px 36px 37px",
                border: plan.primary
                  ? "1px solid #00D1C1"
                  : "1px solid rgba(0, 0, 0, 0.2)",
                borderRadius: 24,
                boxShadow: plan.primary
                  ? "0px 0px 80px -20px rgba(0, 217, 217, 0.3)"
                  : undefined,
                zIndex: plan.popular ? 10 : 1,
              }}
            >
              {plan.popular && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center rounded-lg text-black uppercase font-bold"
                  style={{
                    top: -16,
                    background: "#00D1C1",
                    borderRadius: 8,
                    padding: "7px 18px",
                    fontSize: 11,
                    lineHeight: "18px",
                    letterSpacing: "0.44px",
                    zIndex: 9999,
                  }}
                >
                  MOST POPULAR
                </div>
              )}
              <h3
                className="font-bold text-black"
                style={{ fontSize: 22, lineHeight: "35px" }}
              >
                {plan.name}
              </h3>
              <p
                className="text-black"
                style={{
                  fontSize: 14,
                  lineHeight: "22px",
                  marginTop: 8,
                  marginBottom: 10,
                }}
              >
                {plan.tagline}
              </p>
              <div style={{ marginBottom: 18 }}>
                <span
                  className="font-extrabold text-black"
                  style={{
                    fontSize:
                      plan.price === "Custom" || plan.isFree ? 48 : 40,
                    lineHeight:
                      plan.price === "Custom" || plan.isFree
                        ? "77px"
                        : "64px",
                    letterSpacing: "-1.44px",
                  }}
                >
                  {plan.price}
                </span>
                {plan.period && (
                  <span
                    className="ml-1"
                    style={{
                      fontSize: plan.isFree ? 16 : 15,
                      lineHeight: plan.isFree ? "26px" : "24px",
                      color: plan.isFree ? "#6B7280" : "#000000",
                      fontWeight: plan.isFree ? 500 : 400,
                    }}
                  >
                    {plan.period}
                  </span>
                )}
              </div>
              <ul
                className="flex flex-col"
                style={{ gap: "21px", marginBottom: 24 }}
              >
                {plan.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-black"
                    style={{
                      fontSize: 14,
                      lineHeight: "22px",
                      fontWeight: 400,
                    }}
                  >
                    <Check
                      className="w-[18px] h-[18px] flex-shrink-0 mt-0.5"
                      style={{ color: "#34A851" }}
                      strokeWidth={2.5}
                    />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to={plan.href}
                className="mt-auto flex items-center justify-center w-full text-center text-black font-semibold box-border"
                style={{
                  height: plan.primary ? 49 : 51,
                  borderRadius: 12,
                  fontSize: 14,
                  lineHeight: "17px",
                  ...(plan.primary
                    ? { background: "#00D1C1" }
                    : {
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        background: "transparent",
                      }),
                }}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section
        className="flex flex-col justify-center items-center w-full py-12 sm:py-16 md:py-20 px-4 md:px-[80px]"
        style={{ background: "rgba(0, 0, 0, 0.05)" }}
      >
        <div className="max-w-[1200px] w-full mx-auto px-4 flex flex-col items-center gap-12">
          <div className="text-center">
            <h2 className="font-extrabold text-[48px] leading-[82px] tracking-[-0.96px] text-black mb-4">
              Frequently Asked Questions
            </h2>
            <p
              className="text-[24px] leading-[43px]"
              style={{ color: "rgba(0,0,0,0.6)" }}
            >
              Everything you need to know about how TeamGrid works and the value
              it delivers.
            </p>
          </div>
          <div className="w-full max-w-[1053px] space-y-4">
            {FAQ_ITEMS.map((faq, i) => (
              <div
                key={faq.q}
                className="rounded-xl p-6 cursor-pointer bg-white"
                style={{
                  border:
                    openFaq === i ? "1px solid #28B4AE" : "1px solid #D1D5DB",
                  transition: "border-color 0.15s ease-in-out",
                }}
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  <h3
                    className="font-bold text-[#1A1A1A]"
                    style={{ fontSize: 18, lineHeight: "28px" }}
                  >
                    {faq.q}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                {openFaq === i && (
                  <p
                    className="mt-4"
                    style={{
                      fontSize: 16,
                      lineHeight: "26px",
                      color: "#374151",
                    }}
                  >
                    {faq.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="flex flex-col justify-center items-center w-full py-12 sm:py-16 md:py-[80px] relative px-4 md:px-[24px]"
        style={{ background: "#000000" }}
      >
        <div className="max-w-[1200px] w-full mx-auto flex flex-col lg:flex-row items-center justify-between gap-12">
          <div className="flex flex-col items-start gap-5 max-w-[637px]">
            <h2
              className="font-extrabold text-[48px] leading-[57px] tracking-[-1.56px]"
              style={{ color: "#E5E7EB" }}
            >
              Ready to Boost Team Productivity?
            </h2>
            <p
              className="text-[20px] leading-[30px]"
              style={{ color: "#A0A0A0" }}
            >
              Start using TeamGrid to monitor performance, uncover insights,
              and make smarter workforce decisions.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center py-4 px-8 rounded-lg font-bold text-[15px] leading-6 text-black"
                style={{ background: "#00D1C1" }}
              >
                {/* Start Free Trial */}
                Register
              </Link>
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold text-lg text-white/80 border border-white hover:bg-white/10 transition-all duration-300"
              >
                Talk to Sales
              </Link>
            </div>
            <p
              className="text-[14px] font-medium leading-[22px]"
              style={{ color: "#A0A0A0" }}
            >
              No credit card required • Full feature access • 5-minute setup
            </p>
          </div>
          <div
            className="w-full max-w-[546px] rounded-2xl bg-white overflow-hidden flex-shrink-0"
            style={{ boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.2)" }}
          >
            <div className="p-8 sm:p-10">
              {demoSubmitted ? (
                <div className="flex flex-col items-center justify-center text-center py-8">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(0, 212, 189, 0.15)" }}>
                    <Check className="w-7 h-7" style={{ color: "#00D4BD" }} />
                  </div>
                  <h3 className="font-bold text-[24px] leading-[38px] text-[#1F2937] mb-2">
                    Thank you!
                  </h3>
                  <p className="text-[16px] leading-[24px] text-[#6B7280]">
                    We&apos;ve received your request and will be in touch shortly.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-bold text-[24px] leading-[38px] text-[#1F2937] mb-6">
                    Request a Demo
                  </h3>
                  <form id="demo-request-ai-workforce-intelligence-footer" onSubmit={handleDemoSubmit} className="flex flex-col w-full" style={{ gap: 15 }}>
                    {/* Honeypot field — hidden from real users, bots will fill it */}
                    <input
                      type="text"
                      name="website"
                      style={{ display: "none", visibility: "hidden", position: "absolute", left: "-9999px" }}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                    {[
                      {
                        key: "fullName",
                        label: "Full Name",
                        placeholder: "John Doe",
                        required: true,
                      },
                      {
                        key: "email",
                        label: "Work Email",
                        placeholder: "john@company.com",
                        required: true,
                      },
                      {
                        key: "company",
                        label: "Company Name",
                        placeholder: "Acme Inc.",
                        required: true,
                      },
                      {
                        key: "phone",
                        label: "Phone Number",
                        placeholder: "9876543210",
                        required: true,
                      },
                    ].map(({ key, label, placeholder, required }) => (
                      <div key={key} className="flex flex-col w-full" style={{ gap: 8 }}>
                        <label
                          className="block w-full"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 500,
                            fontSize: 14,
                            lineHeight: "22px",
                            color: "#374151",
                          }}
                        >
                          {label}{required && <span style={{ color: "#EF4444", marginLeft: 2 }}>*</span>}
                        </label>
                        <input
                          type={key === "email" ? "email" : "text"}
                          inputMode={key === "phone" ? "numeric" : undefined}
                          maxLength={key === "phone" ? 10 : undefined}
                          value={demoForm[key]}
                          onChange={(e) => {
                            const val = key === "phone"
                              ? e.target.value.replace(/[^0-9]/g, "").slice(0, 10)
                              : e.target.value;
                            setDemoForm((s) => ({ ...s, [key]: val }));
                            if (formErrors[key]) setFormErrors((prev) => ({ ...prev, [key]: undefined }));
                          }}
                          placeholder={placeholder}
                          className="w-full rounded-lg border bg-white box-border placeholder:text-[#6B7280]"
                          style={{
                            padding: 12,
                            fontFamily: "Arial, sans-serif",
                            fontWeight: 400,
                            fontSize: 16,
                            lineHeight: "18px",
                            color: "#1F2937",
                            borderColor: (formErrors[key] && activeErrorFormId === "demo-request-ai-workforce-intelligence-footer") ? "#EF4444" : "#E5E7EB",
                          }}
                        />
                        {formErrors[key] && activeErrorFormId === "demo-request-ai-workforce-intelligence-footer" && (
                          <span style={{ color: "#EF4444", fontSize: 12, marginTop: 2 }}>
                            {formErrors[key]}
                          </span>
                        )}
                      </div>
                    ))}
                    <div className="flex justify-center">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(token) => setRecaptchaToken(token)}
                        onExpired={() => setRecaptchaToken(null)}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={demoSubmitting || !recaptchaToken}
                      className="w-full rounded-lg text-center font-bold text-black disabled:opacity-70 disabled:cursor-not-allowed"
                      style={{
                        background: "#00D4BD",
                        padding: "13px 24px 12px",
                        height: 40,
                        fontFamily: "Arial, sans-serif",
                        fontWeight: 700,
                        fontSize: "13.3px",
                        lineHeight: "15px",
                      }}
                    >
                      {demoSubmitting ? "Submitting..." : "Request a Demo"}
                    </button>
                  </form>
                  <p className="flex items-center justify-center gap-1.5 mt-4 text-[12px] leading-[19px] text-[#6B7280]">
                    <Lock className="w-3 h-3" />
                    We respect your privacy. Your info is safe.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {!isAiWorkforce && <Footer />}
    </div>
  );
};

export default AlternateLanding;
